<template>
  <div
    :id="id"
    class="rs-form-settings-select-options"
  >
    <RsDragndrop
      id="drag_content_options"
      group-name="options_drag"
      behaviour="move"
      :get-child-payload="getDragItem"
      drag-class="card-ghost"
      drop-class="card-ghost-drop"
      :drop-placeholder="dropPlaceholderOptions"
      @drop="handleDropItem"
    >
      <RsDragger
        v-for="(opt, index) in options"
        :id="`dragger_options_${index}`"
        :key="index"
        class="row mx-0 pb-2 align-items-center"
      >
        <div class="col-12 p-0 row m-0 ">
          <!-- ICON TO DRAG -->
          <div
            class="flex-grow-0 cursor-grabbing draggable-card"
            :has-drag="true"
          >
            <i class="fas fa-grip-vertical rs-text-secondary-light" />
          </div>
          <div
            v-show="type !== 'checkbox-group'"
            class="p-0 flex-grow-0 ml-2"
            :title="$ts('formSettings.fieldConfig.toggleDefaultOption')"
          >
            <RsFormRadio
              :id="`${id}_${index}_radio_selected`"
              :model-value="options[index].selected"
              :value="true"
              @change="handleRadioChanged(index)"
            />
          </div>
          <!-- CHECKBOX -->
          <div
            v-show="type === 'checkbox-group'"
            class="p-0 flex-grow-0 ml-2"
            :title="$ts('formSettings.fieldConfig.toggleDefaultOption')"
          >
            <RsFormCheckbox
              :id="`${id}_${index}_checkbox_selected`"
              v-model="options[index].selected"
              :true-value="true"
              :false-value="false"
            />
          </div>
          <div class="col p-0 row m-0">
            <!-- LABEL -->
            <div class="col-6 pr-0 pl-3">
              <input
                v-model.lazy.trim="opt.label"
                type="text"
                :placeholder="$ts('formSettings.fieldConfig.optionLabelPlaceholder')"
                class="form-control"
              >
            </div>
            <!-- VALUE -->
            <div class="col-6 pr-0 pl-3">
              <input
                v-model.lazy.trim="opt.value"
                type="text"
                :placeholder="$ts('formSettings.fieldConfig.optionValuePlaceholder')"
                class="form-control"
                @blur="handleValueBlur(index, opt.value)"
              >
            </div>
          </div>
          <!-- DELETE ICON -->
          <div
            class="p-0 flex-grow-0 ml-2"
          >
            <RsButton
              :style="{ visibility: type !== 'select' && index === 0 ? 'hidden' : 'initial' }"
              class="btn-toolbar-inline sm"
              @click="options.splice(index, 1)"
            >
              <i class="far fa-trash-alt rs-text-error" />
            </RsButton>
          </div>
        </div>
      </RsDragger>
    </RsDragndrop>
    <RsButton
      class="btn-toolbar-inline sm add-option"
      @click="options.push({ label: '', value: '', selected: false })"
    >
      <div class="d-flex align-items-center">
        <i class="far fa-plus d-block" />
        <span class="d-block">{{ $ts('formSettings.fieldConfig.addOption') }}</span>
      </div>
    </RsButton>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { formatToOptionValue } from '@/shared';

export type TFormSettingSelectOptions = {
  label: string,
  value: string,
  selected?: boolean,
};

export default defineComponent({
  name: 'RsFormSettingsSelectOptions',

  props: {
    id: {
      type: String,
      required: true,
    },

    value: {
      type: Array as PropType<TFormSettingSelectOptions[]>,
      default: () => [],
    },

    type: {
      type: String,
      default: 'select',
    },
  },

  data() {
    return {
      dropPlaceholderOptions: {
        className: 'drop-preview',
        animationDuration: '150',
        showOnTop: false,
      },
      dragType: '' as string,
      options: this.value,
      isAssigned: false as boolean,
    };
  },
  watch: {
    value: {
      handler() {
        if (!this.isAssigned) {
          this.options = this.value;
          this.isAssigned = true;

          if (this.type !== 'checkbox-group') {
            this.handleRadioChanged(this.value.findIndex((v: any) => v.selected));
          }
        }
      },
    },

    options: {
      deep: true,
      handler() {
        this.$emit('input', this.options);
      },
    },
  },

  mounted() {
    if (this.type !== 'checkbox-group') {
      this.handleRadioChanged(this.value.findIndex((v: any) => v.selected));
    }
  },

  methods: {
    // Here we should re order the options based on the current value
    move(arr: any, from: number, to: number): Array<any> {
      return arr.splice(to, 0, arr.splice(from, 1)[0]);
    },
    handleDropItem(dropResult: any): void {
      this.move(this.options, dropResult.removedIndex, dropResult.addedIndex);
    },
    handleRadioChanged(selectedIdx: number) {
      this.options.forEach((o: any, idx: number) => {
        (this.options as any)[idx].selected = false;
      });

      (this.options as any)[selectedIdx].selected = true;
      this.$forceUpdate();
    },
    handleValueBlur(index: number, value: string) {
      (this.options[index] as any).value = formatToOptionValue(value);
    },
    getDragItem(index: number): any {
      this.dragType = 'sidebar';
      return this.options[index];
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables";

.rs-form-settings-select-options {
  .rs-radio {
    min-height: auto;

    label {
      display: block;
      height: 16px;
    }
  }
  .add-option {
    color: $tertiary-muted !important;
    opacity: 0.7;
    font-size: 14px !important;
  }
}

</style>
